/**
 * TeamManagement Component — Phase 4c (Refined)
 *
 * Allows administrators to:
 * - List all team members with email, role, status, join date
 * - Edit users (change role, deactivate/reactivate)
 * - Invite users (placeholder for future implementation)
 *
 * Permission-gated to USER_MANAGE. Owner protection prevents:
 * - Non-owners modifying owners
 * - Non-owners changing their own role
 * - Non-owners deactivating themselves or owners
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import PageHeader from '../components/ui/PageHeader';
import { hasPermission, PERMISSIONS, handleAPIError, getCurrentUser } from '../authUtils';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';
import { ErrorMessage, PermissionDeniedMessage } from './ErrorMessage';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const ROLE_DISPLAY_NAMES = {
  admin: 'Owner',
  administrator: 'Administrator',
  underwriter: 'Underwriter',
  viewer: 'Viewer',
};

const ASSIGNABLE_ROLES = ['administrator', 'underwriter', 'viewer'];

/**
 * Derive a display name from email address.
 * Example: john.smith@example.com → John Smith
 */
function deriveNameFromEmail(email) {
  if (!email) return '—';
  const localPart = email.split('@')[0]; // Get part before @
  const nameParts = localPart
    .split(/[._-]/) // Split by ., _, or -
    .filter(part => part.length > 0)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
  return nameParts.length > 0 ? nameParts.join(' ') : '—';
}

export default function TeamManagement() {
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('list', '/dashboard');
  const currentUser = getCurrentUser();
  const userCanManageTeam = hasPermission(PERMISSIONS.USER_MANAGE);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [editingIsActive, setEditingIsActive] = useState(null);
  const [savingUserId, setSavingUserId] = useState(null);
  const [filter, setFilter] = useState('');

  // Call useEffect UNCONDITIONALLY (moved outside the permission check)
  useEffect(() => {
    // Inside the effect, check permission before fetching
    if (!userCanManageTeam) {
      setLoading(false);
      return;
    }
    fetchUsers();
  }, [userCanManageTeam]);

  // Set up unified header
  useEffect(() => {
    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: 'Team Management',
      subtitle: null,
      actions: [],
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderConfig, resetHeaderConfig]);

  // Render permission-denied message if user lacks permission (after all hooks)
  if (!userCanManageTeam) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <PageHeader
            title="Team Management"
            subtitle="Manage your team members"
          />
          <div style={styles.card}>
            <PermissionDeniedMessage action="manage team members" />
          </div>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUsers(response.data);
      setError(null);
    } catch (err) {
      const processedError = handleAPIError(err);
      setError(err);
      console.error('Failed to load team members:', processedError);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setEditingUserId(userId);
      setEditingRole(user.role);
      setEditingIsActive(user.is_active);
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingRole(null);
    setEditingIsActive(null);
  };

  const handleSaveUser = async (userId) => {
    try {
      setSavingUserId(userId);
      const token = localStorage.getItem('token');

      // Build update payload with changes
      const updateData = {};
      const currentUser = users.find(u => u.id === userId);
      if (currentUser.role !== editingRole) {
        updateData.role = editingRole;
      }

      // Note: is_active is updated via deactivate/reactivate endpoints
      const statusChanged = currentUser.is_active !== editingIsActive;

      // First, update role if changed
      if (Object.keys(updateData).length > 0) {
        await axios.patch(`${API_URL}/users/${userId}`, updateData, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      }

      // Then, update status if changed
      if (statusChanged) {
        if (editingIsActive && !currentUser.is_active) {
          // Reactivate
          await axios.post(`${API_URL}/users/${userId}/reactivate`, {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
        } else if (!editingIsActive && currentUser.is_active) {
          // Deactivate
          const confirmed = window.confirm('Are you sure you want to deactivate this user? They will not be able to log in.');
          if (!confirmed) {
            setSavingUserId(null);
            return;
          }
          await axios.post(`${API_URL}/users/${userId}/deactivate`, {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
        }
      }

      // Update local state
      setUsers(users.map(u => u.id === userId
        ? { ...u, role: editingRole, is_active: editingIsActive }
        : u
      ));
      setEditingUserId(null);
      setEditingRole(null);
      setEditingIsActive(null);
    } catch (err) {
      const processedError = handleAPIError(err);
      setError(err);
      console.error('Failed to update user:', processedError);
    } finally {
      setSavingUserId(null);
    }
  };

  const filteredUsers = users.filter(u =>
    (u.email?.toLowerCase().includes(filter.toLowerCase()))
  );

  const sortedUsers = [...filteredUsers].sort((a, b) =>
    (a.email || '').localeCompare(b.email || '')
  );

  if (loading) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <PageHeader
            title="Team Management"
            subtitle="Manage your team members"
          />
          <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.TEXT_LIGHT }}>
            <p style={TYPOGRAPHY.BODY}>Loading team members...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        <ErrorMessage error={error} onDismiss={() => setError(null)} />

        {/* Page Header */}
        <PageHeader
          title="Team Management"
          subtitle="Manage your team members and roles"
          primaryAction={
            <button
              disabled
              style={{...styles.primaryButton, opacity: 0.6, cursor: 'not-allowed'}}
              title="User invitations coming soon"
            >
              + Invite User (coming soon)
            </button>
          }
        />

        {/* Search Bar */}
        <div style={styles.searchBar}>
          <input
            type="text"
            placeholder="Search by email..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.searchInput}
          />
          <span style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_LIGHT}}>
            Showing {sortedUsers.length} of {users.length} team member{users.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Content Area */}
        {sortedUsers.length === 0 ? (
          <div style={styles.card}>
            <div style={{ textAlign: 'center', padding: SPACING.XXXL }}>
              <h3 style={{...TYPOGRAPHY.H3, color: COLORS.TEXT_LIGHT, margin: 0, marginBottom: SPACING.MD}}>
                No team members found
              </h3>
              {filter ? (
                <p style={{...TYPOGRAPHY.BODY, color: COLORS.TEXT_LIGHT, margin: 0}}>
                  Try adjusting your search filters.
                </p>
              ) : (
                <p style={{...TYPOGRAPHY.BODY, color: COLORS.TEXT_LIGHT, margin: 0}}>
                  Invite team members to manage access and roles.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div style={styles.card}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.headerCell}>Name</th>
                  <th style={styles.headerCell}>Email</th>
                  <th style={styles.headerCell}>Role</th>
                  <th style={styles.headerCell}>Status</th>
                  <th style={styles.headerCell}>Joined</th>
                  <th style={styles.headerCell}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user, idx) => (
                  <tr
                    key={user.id}
                    style={{
                      ...styles.tableRow,
                      borderBottom: idx < sortedUsers.length - 1 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.BG_SURFACE_SECONDARY}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Name Column (derived from email) */}
                    <td style={{...styles.tableCell, fontWeight: '600'}}>
                      {deriveNameFromEmail(user.email)}
                    </td>

                    {/* Email Column */}
                    <td style={styles.tableCell}>
                      {user.email}
                      {user.is_owner && (
                        <div style={styles.ownerBadge}>👑 Owner</div>
                      )}
                    </td>

                    {/* Role Column (editable in edit mode, but not for self) */}
                    <td style={styles.tableCell}>
                      {editingUserId === user.id && currentUser?.is_owner ? (
                        <select
                          value={editingRole}
                          onChange={(e) => setEditingRole(e.target.value)}
                          style={{...styles.roleSelect, opacity: user.id === currentUser?.user_id ? 0.6 : 1}}
                          disabled={savingUserId === user.id || user.id === currentUser?.user_id}
                          title={user.id === currentUser?.user_id ? "You cannot change your own role" : ""}
                        >
                          {ASSIGNABLE_ROLES.map(role => (
                            <option key={role} value={role}>
                              {ROLE_DISPLAY_NAMES[role]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>{ROLE_DISPLAY_NAMES[user.role] || user.role}</span>
                      )}
                    </td>

                    {/* Status Column (editable in edit mode, but not for self) */}
                    <td style={styles.tableCell}>
                      {editingUserId === user.id && currentUser?.is_owner ? (
                        <div style={styles.statusToggle}>
                          <label style={{...styles.toggleLabel, opacity: user.id === currentUser?.user_id ? 0.6 : 1}}>
                            <input
                              type="checkbox"
                              checked={editingIsActive}
                              onChange={(e) => setEditingIsActive(e.target.checked)}
                              disabled={savingUserId === user.id || user.id === currentUser?.user_id}
                              style={{marginRight: SPACING.XS, cursor: user.id === currentUser?.user_id ? 'not-allowed' : 'pointer'}}
                              title={user.id === currentUser?.user_id ? "You cannot change your own status" : ""}
                            />
                            Active
                          </label>
                        </div>
                      ) : (
                        <>
                          {user.is_active ? (
                            <span style={styles.activeBadge}>✓ Active</span>
                          ) : (
                            <span style={styles.inactiveBadge}>✗ Inactive</span>
                          )}
                        </>
                      )}
                    </td>

                    {/* Joined Column */}
                    <td style={styles.tableCell}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>

                    {/* Action Column */}
                    <td style={styles.tableCell}>
                      {currentUser?.is_owner && (!user.is_owner || user.id === currentUser?.user_id) ? (
                        editingUserId === user.id ? (
                          // Edit mode: Save/Cancel buttons
                          <div style={styles.actionGroup}>
                            {user.id === currentUser?.user_id ? (
                              // Self-edit: Show disabled Save with helper message
                              <div style={styles.selfEditHelper}>
                                <button
                                  onClick={() => handleSaveUser(user.id)}
                                  style={{...styles.editButton, ...styles.successButton, opacity: 0.5}}
                                  disabled={true}
                                  title="You cannot edit your own role or status"
                                >
                                  Save (locked)
                                </button>
                              </div>
                            ) : (
                              // Other users: Normal Save button
                              <button
                                onClick={() => handleSaveUser(user.id)}
                                style={{...styles.editButton, ...styles.successButton}}
                                disabled={savingUserId === user.id}
                              >
                                {savingUserId === user.id ? 'Saving...' : 'Save'}
                              </button>
                            )}
                            <button
                              onClick={handleCancelEdit}
                              style={styles.editButton}
                              disabled={savingUserId === user.id}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          // View mode: Edit button
                          <button
                            onClick={() => handleEditUser(user.id)}
                            style={styles.editButton}
                          >
                            Edit
                          </button>
                        )
                      ) : (
                        <span style={styles.readOnlyText}>
                          {user.is_owner ? 'Owner' : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    backgroundColor: COLORS.BG_PAGE_SURFACE,
    minHeight: '100vh',
  },

  container: {
    padding: `${SPACING.XXL} ${SPACING.XL}`,
    maxWidth: '1400px',
    margin: '0 auto',
  },

  primaryButton: {
    padding: `${SPACING.MD} ${SPACING.XL}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    textDecoration: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'inline-block',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(77, 219, 148, 0.3)',
    letterSpacing: '0.3px',
  },

  searchBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: SPACING.LG,
    marginBottom: SPACING.XL,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    borderRadius: RADIUS.DEFAULT,
  },

  searchInput: {
    flex: 1,
    maxWidth: '350px',
    padding: `${SPACING.SM} ${SPACING.MD}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },

  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    boxShadow: SHADOWS.DEFAULT,
    overflow: 'hidden',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  headerRow: {
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  headerCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    textAlign: 'left',
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  tableRow: {
    transition: 'background-color 0.15s ease',
  },

  tableCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    color: COLORS.TEXT_PRIMARY,
    verticalAlign: 'middle',
  },

  ownerBadge: {
    display: 'inline-block',
    marginTop: SPACING.XS,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: '#d97706',
    fontWeight: '600',
    backgroundColor: 'rgba(217, 119, 6, 0.1)',
    padding: `2px ${SPACING.SM}`,
    borderRadius: RADIUS.SM,
  },

  activeBadge: {
    display: 'inline-block',
    padding: `${SPACING.XS} ${SPACING.SM}`,
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    color: '#059669',
    borderRadius: RADIUS.SM,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '600',
  },

  inactiveBadge: {
    display: 'inline-block',
    padding: `${SPACING.XS} ${SPACING.SM}`,
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    color: '#dc2626',
    borderRadius: RADIUS.SM,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '600',
  },

  editButton: {
    padding: `${SPACING.XS} ${SPACING.MD}`,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    backgroundColor: COLORS.PRIMARY,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.SM,
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },

  successButton: {
    backgroundColor: '#10b981',
  },

  actionGroup: {
    display: 'flex',
    gap: SPACING.SM,
  },

  roleSelect: {
    padding: `${SPACING.XS} ${SPACING.SM}`,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.SM,
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    cursor: 'pointer',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  readOnlyText: {
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_LIGHT,
  },

  statusToggle: {
    display: 'flex',
    alignItems: 'center',
  },

  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    color: COLORS.TEXT_PRIMARY,
    cursor: 'pointer',
    userSelect: 'none',
  },

  selfEditHelper: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SM,
  },
};
